import {useMemo, useState} from "react";
import {Box, Button, Stack, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import {appStyles} from "../../../../AppStyles.ts";
import {StyledInputBase, StyledSearch, StyledSearchIconWrapper} from "../../../AppStyledComponents.tsx";
import type {Resolver, ResolverType} from "../../../../sdo/Resolver.ts";
import {RESOLVER_TYPE_META} from "../../../../sdo/Resolver.ts";
import ResolverCard from "./resolvers/ResolverCard.tsx";
import ResolverCreateBar from "./resolvers/ResolverCreateBar.tsx";

let nextId = 100;

function createEmptyResolver(type: ResolverType): Resolver {
    const base = {uid: String(nextId++), explicit: true, weight: 100};
    switch (type) {
        case 'ADDRESS_PART':
            return {...base, type, streetNumber: "", streetName: "", aptSuite: "", city: "", state: "", postalCode: "", country: ""};
        case 'CHARACTER':
            return {...base, type, operator: "CONTAINS", value: ""};
        case 'FIELD':
            return {...base, type, fieldName: "", operator: "EXACT", fieldValue: ""};
        case 'GEOFENCE':
            return {...base, type, swLatitude: 0, swLongitude: 0, neLatitude: 0, neLongitude: 0};
        case 'HASH':
            return {...base, type, hashType: "SHA256", hashValue: ""};
        case 'INTELLIGENCE':
            return {...base, type, intelType: "", definition: "", display: "", matchHash: ""};
        case 'MEASUREMENT':
            return {...base, type, measurementType: "", unit: "", value: 0, precision: 0};
        case 'NAME_PART':
            return {...base, type, prefix: "", firstName: "", middleName: "", lastName: "", suffix: "", shortName: "", nickname: ""};
        case 'NUMERIC':
            return {...base, type, value: 0, precision: 0, enforceType: false, numericType: ""};
        case 'REGEX':
            return {...base, type, pattern: ""};
        case 'TEMPORAL':
            return {
                ...base, type, dateType: "DATE", dateRange: "SPECIFIC",
                fromMonth: "", fromDay: "", fromYear: "", fromHour: "", fromMinute: "", fromAP: "AM",
                toMonth: "", toDay: "", toYear: "", toHour: "", toMinute: "", toAP: "AM",
            };
        case 'WORD':
            return {
                ...base, type, operator: "WORD", value: "", inputLang: "ENGLISH",
                alphaMatch: false, charMatch: false, translate: false, misspelled: false,
                acronym: false, abbreviation: false,
                containsType: "ANY", containsMin: 0, containsDistance: "ANY", containsDistanceMax: 0,
            };
    }
}

function buildTestData(): Resolver[] {
    return [
        {
            uid: "1", type: "ADDRESS_PART", explicit: true, weight: 100,
            streetNumber: "4322", streetName: "Market St", aptSuite: "3450",
            city: "San Francisco", state: "California", postalCode: "94111", country: "US",
        },
        {
            uid: "2", type: "CHARACTER", explicit: false, weight: 80,
            operator: "CONTAINS", value: "anna",
        },
        {
            uid: "3", type: "FIELD", explicit: true, weight: 100,
            fieldName: "Incorporator", operator: "EXACT", fieldValue: "Tom Jones",
        },
        {
            uid: "4", type: "GEOFENCE", explicit: true, weight: 90,
            swLatitude: 37.7049, swLongitude: -122.5194, neLatitude: 37.7749, neLongitude: -122.3894,
        },
        {
            uid: "5", type: "HASH", explicit: true, weight: 100,
            hashType: "SHA256", hashValue: "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855",
        },
        {
            uid: "6", type: "INTELLIGENCE", explicit: true, weight: 100,
            intelType: "SELECTOR", definition: "NET.EMAIL_ADDRESS", display: "john@example.com", matchHash: "a1b2c3d4",
        },
        {
            uid: "7", type: "MEASUREMENT", explicit: false, weight: 75,
            measurementType: "SPEED", unit: "METERS_PER_SECOND", value: 33.3333, precision: 4,
        },
        {
            uid: "8", type: "NAME_PART", explicit: true, weight: 100,
            prefix: "Dr.", firstName: "Thomas", middleName: "Alexander",
            lastName: "Hamilton", suffix: "MD", shortName: "T. Hamilton", nickname: "Tom",
        },
        {
            uid: "9", type: "NUMERIC", explicit: true, weight: 100,
            value: 3.14, precision: 2, enforceType: false, numericType: "",
        },
        {
            uid: "10", type: "REGEX", explicit: true, weight: 100,
            pattern: "([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}",
        },
        {
            uid: "11", type: "TEMPORAL", explicit: true, weight: 100,
            dateType: "DATE", dateRange: "RANGE",
            fromMonth: "5", fromDay: "23", fromYear: "2026", fromHour: "", fromMinute: "", fromAP: "AM",
            toMonth: "5", toDay: "28", toYear: "2026", toHour: "", toMinute: "", toAP: "AM",
        },
        {
            uid: "12", type: "WORD", explicit: false, weight: 85,
            operator: "WORD", value: "Anna", inputLang: "ENGLISH",
            alphaMatch: true, charMatch: false, translate: false, misspelled: false,
            acronym: false, abbreviation: false,
            containsType: "ANY", containsMin: 0, containsDistance: "ANY", containsDistanceMax: 0,
        },
    ];
}

function UXIntelResolversPanel() {
    const [resolvers, setResolvers] = useState<Resolver[]>(buildTestData);
    const [filterText, setFilterText] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [newResolverUid, setNewResolverUid] = useState<string | null>(null);

    const filtered = useMemo(() => {
        if (!filterText.trim()) return resolvers;
        const q = filterText.toLowerCase();
        return resolvers.filter((r) => {
            const typeName = RESOLVER_TYPE_META[r.type].displayName.toLowerCase();
            if (typeName.includes(q)) return true;
            return JSON.stringify(r).toLowerCase().includes(q);
        });
    }, [resolvers, filterText]);

    const handleDelete = (uid: string) => {
        setResolvers((prev) => prev.filter((r) => r.uid !== uid));
    };

    const handleUpdate = (updated: Resolver) => {
        setResolvers((prev) => prev.map((r) => r.uid === updated.uid ? updated : r));
    };

    const handleCreate = (type: ResolverType) => {
        const newResolver = createEmptyResolver(type);
        setResolvers((prev) => [newResolver, ...prev]);
        setNewResolverUid(newResolver.uid);
        setShowCreate(false);
    };

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            {/* Title */}
            <Stack direction="row" padding={1} justifyContent="center">
                <Typography sx={appStyles.panelSectionTitle}>Resolvers</Typography>
            </Stack>

            {/* Filter bar */}
            <Stack direction="row" spacing={1} padding={1} alignItems="center" justifyContent="center" sx={{position: "relative"}}>
                <StyledSearch>
                    <StyledSearchIconWrapper>
                        <SearchIcon sx={{fontSize: 16, color: "#7093a0"}}/>
                    </StyledSearchIconWrapper>
                    <StyledInputBase
                        placeholder="Filter resolvers..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </StyledSearch>

                <Typography sx={appStyles.panelFilterCount}>
                    Showing {filtered.length} of {resolvers.length}
                </Typography>

                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon/>}
                    onClick={() => setShowCreate(!showCreate)}
                    sx={{...appStyles.panelActionButton, position: "absolute", right: 8}}
                >
                    New Resolver
                </Button>
            </Stack>

            {/* Create bar */}
            {showCreate && (
                <Box sx={{paddingX: 1}}>
                    <ResolverCreateBar
                        onSelect={handleCreate}
                        onClose={() => setShowCreate(false)}
                    />
                </Box>
            )}

            {/* Resolver cards list */}
            <Stack direction="column" spacing={1} sx={{...appStyles.defaultPanelCardsWrapper, padding: "8px", alignItems: "stretch"}}>
                {filtered.map((resolver) => (
                    <ResolverCard
                        key={resolver.uid}
                        resolver={resolver}
                        onDelete={handleDelete}
                        onUpdate={handleUpdate}
                        startInEditMode={resolver.uid === newResolverUid}
                    />
                ))}
                {filtered.length === 0 && (
                    <Typography sx={{
                        color: "rgba(242, 245, 250, 0.4)",
                        fontSize: "0.80rem",
                        textAlign: "center",
                        padding: "24px",
                    }}>
                        No resolvers match the current filter.
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

export default UXIntelResolversPanel;
